import React, { useCallback } from 'react';
import { useFormContext } from '../context/FormContext';
import { FormActionType, DeleteItem } from '../context/formReducer';
import { ControlModeTemplateField } from '@src/types/ControlModes';
import { SelectChangeEvent } from '@mui/material';

// Hook for form-related operations
export function useFormOperations() {
  const { state, dispatch } = useFormContext();

  // Extract form-related state
  const {
    name,
    formValues,
    originalValues,
    unsavedSettings,
    hasAttemptedSubmit,
    isSaving,
    fieldErrors,
  } = state;

  // Set name
  const setName = useCallback(
    (name: string) => {
      dispatch({ type: FormActionType.SET_NAME, payload: name });
    },
    [dispatch],
  );

  // Handle input changes
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value, checked, type } = event.target;
      dispatch({
        type: FormActionType.UPDATE_FORM_VALUE,
        payload: {
          name,
          value: type === 'checkbox' ? checked : value,
          defaultValue: type === 'checkbox' ? checked : value,
        },
      });
    },
    [dispatch],
  );

  // Handle select changes
  const handleSelectChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const { name, value } = event.target;
      dispatch({
        type: FormActionType.UPDATE_FORM_VALUE,
        payload: { name, value, defaultValue: value },
      });
    },
    [dispatch],
  );

  // Toggle quick config
  const handleQuickConfigToggle = useCallback(
    (field: ControlModeTemplateField) => {
      if (!unsavedSettings) {
        return;
      }

      // Create a copy of unsavedSettings
      const updatedSettings = [...unsavedSettings];

      // Find the setting that contains this field
      for (let i = 0; i < updatedSettings.length; i++) {
        const setting = updatedSettings[i];
        const fieldIndex = setting.fields.findIndex(
          (f) => f.controllerField === field.controllerField,
        );

        if (fieldIndex !== -1) {
          // Toggle the quickConfig property
          const updatedField = {
            ...setting.fields[fieldIndex],
            quickConfig: !setting.fields[fieldIndex].quickConfig,
          };

          // Update the field in the setting
          setting.fields[fieldIndex] = updatedField;

          // Update unsavedSettings
          dispatch({
            type: FormActionType.SET_UNSAVED_SETTINGS,
            payload: updatedSettings,
          });

          // Show notification
          dispatch({
            type: FormActionType.SHOW_SNACKBAR,
            payload: {
              message: `Field "${field.uiLabel}" ${updatedField.quickConfig ? 'added to' : 'removed from'} quick config. Click Save to apply changes.`,
              severity: 'info',
            },
          });

          break;
        }
      }
    },
    [dispatch, unsavedSettings],
  );

  // Delete field
  const deleteField = useCallback(
    (field: ControlModeTemplateField) => {
      if (!unsavedSettings) {
        return;
      }

      // Create a copy of unsavedSettings
      const updatedSettings = [...unsavedSettings];

      // Find the setting that contains this field
      for (let i = 0; i < updatedSettings.length; i++) {
        const setting = updatedSettings[i];
        const fieldIndex = setting.fields.findIndex(
          (f) => f.controllerField === field.controllerField,
        );

        if (fieldIndex !== -1) {
          // Remove the field from the setting
          setting.fields.splice(fieldIndex, 1);

          // Update unsavedSettings
          dispatch({
            type: FormActionType.SET_UNSAVED_SETTINGS,
            payload: updatedSettings,
          });

          // Update groupedFields to reflect the deletion
          const updatedGroupedFields = state.groupedFields.map((group) =>
            group.controllerField === setting.controllerField
              ? { ...group, fields: setting.fields }
              : group,
          );

          dispatch({
            type: FormActionType.SET_GROUPED_FIELDS,
            payload: updatedGroupedFields,
          });

          // Show notification
          dispatch({
            type: FormActionType.SHOW_SNACKBAR,
            payload: {
              message: `Field "${field.uiLabel}" removed. Click Save to apply changes.`,
              severity: 'info',
            },
          });

          break;
        }
      }
    },
    [dispatch, unsavedSettings, state.groupedFields],
  );

  // Show delete confirmation
  const showDeleteConfirm = useCallback(
    (itemType: 'field' | 'setting', item: DeleteItem) => {
      dispatch({
        type: FormActionType.SHOW_DELETE_CONFIRM,
        payload: { itemType, item },
      });
    },
    [dispatch],
  );

  // Hide delete confirmation
  const hideDeleteConfirm = useCallback(() => {
    dispatch({ type: FormActionType.HIDE_DELETE_CONFIRM });
  }, [dispatch]);

  // Reset form
  const resetForm = useCallback(() => {
    dispatch({ type: FormActionType.RESET_FORM });
  }, [dispatch]);

  return {
    // State
    name,
    formValues,
    originalValues,
    unsavedSettings,
    hasAttemptedSubmit,
    isSaving,
    fieldErrors,

    // Handlers
    setName,
    handleChange,
    handleSelectChange,
    handleQuickConfigToggle,
    deleteField,
    showDeleteConfirm,
    hideDeleteConfirm,
    resetForm,
  };
}
